import React, { useRef, useState } from 'react';
import { AlertCircle, ChevronDown, Eye, EyeOff, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/* Form pieces shared by the training sign-up and sign-in. */

export const FieldLabel = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <span className="mb-[5px] block text-[12px] font-semibold leading-[1.3] text-[#1A2130]">
    {children}
    {required && <span className="text-[#C62828]"> *</span>}
  </span>
);

export const boxClass =
  'flex h-[38px] w-full items-center gap-[9px] rounded-[10px] border border-[#C4CCD6] bg-white px-3 focus-within:border-[1.5px] focus-within:border-[#0C51E6] focus-within:shadow-[0_0_0_4px_rgba(12,81,230,.12)]';

export const TextField = ({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
}) => {
  const { t } = useTranslation('Signup');
  const [reveal, setReveal] = useState(false);
  const isPassword = type === 'password';
  return (
    <label className="block min-w-0 flex-1">
      <FieldLabel required>{label}</FieldLabel>
      <span className={boxClass}>
        <input
          type={isPassword && reveal ? 'text' : type}
          value={value}
          required
          placeholder={placeholder}
          autoComplete={autoComplete}
          onChange={e => onChange(e.target.value)}
          className="min-w-0 flex-1 border-none bg-transparent p-0 text-[14px] text-[#1A2130] outline-none placeholder:text-[#9AA4B2]"
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setReveal(r => !r)}
            aria-label={reveal ? t('common.hidePassword') : t('common.showPassword')}
            className="text-[#656F7D]"
          >
            {reveal ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </span>
    </label>
  );
};

// An option is a plain value, or a stored value with a translation key for its label (Signup namespace).
export type SelectOption = string | { value: string; label: string };

export const SelectField = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (v: string) => void;
}) => {
  const { t } = useTranslation('Signup');
  return (
    <label className="block min-w-0 flex-1">
      <FieldLabel required>{label}</FieldLabel>
      <span className={`${boxClass} relative`}>
        <select
          value={value}
          required
          onChange={e => onChange(e.target.value)}
          className={`min-w-0 flex-1 appearance-none border-none bg-transparent p-0 pr-5 text-[14px] outline-none ${
            value ? 'text-[#1A2130]' : 'text-[#656F7D]'
          }`}
        >
          <option value="" disabled>
            {t('common.select')}
          </option>
          {options.map(o => {
            const optionValue = typeof o === 'string' ? o : o.value;
            return (
              <option key={optionValue} value={optionValue}>
                {typeof o === 'string' ? o : t(o.label)}
              </option>
            );
          })}
        </select>
        <ChevronDown size={15} className="pointer-events-none absolute right-3 text-[#656F7D]" />
      </span>
    </label>
  );
};

export const Button = ({
  children,
  variant = 'primary',
  loading,
  disabled,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary'; loading?: boolean }) => (
  <button
    {...props}
    disabled={disabled || loading}
    className={`inline-flex h-10 w-full items-center justify-center gap-[9px] whitespace-nowrap rounded-[12px] px-5 text-[14px] font-semibold transition-colors ${
      disabled
        ? 'bg-[#E7ECF3] text-[#9AA4B2]'
        : variant === 'primary'
        ? 'bg-[#0C51E6] text-white hover:bg-[#0940B8]'
        : 'border border-[#C9D6EE] bg-white text-[#0C51E6] hover:bg-[#F7FAFF]'
    } ${loading ? 'cursor-progress opacity-80' : ''}`}
  >
    {loading && <span className="h-[15px] w-[15px] animate-spin rounded-full border-2 border-current border-t-transparent" />}
    {children}
  </button>
);

export const Hint = ({ tone = 'info', children }: { tone?: 'info' | 'error'; children: React.ReactNode }) => (
  <div className="flex items-start gap-[5px]" role={tone === 'error' ? 'alert' : undefined}>
    {tone === 'error' ? (
      <AlertCircle size={13} className="mt-[1px] flex-none text-[#C62828]" />
    ) : (
      <Info size={13} className="mt-[1px] flex-none text-[#0C51E6]" />
    )}
    <span className={`text-[12px] leading-[1.35] ${tone === 'error' ? 'font-medium text-[#C62828]' : 'text-[#5B6472]'}`}>
      {children}
    </span>
  </div>
);

export const CodeInput = ({ value, onChange, large }: { value: string; onChange: (v: string) => void; large?: boolean }) => {
  const { t } = useTranslation('Signup');
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const set = (i: number, digit: string) => {
    const next = (value.slice(0, i) + digit + value.slice(i + 1)).slice(0, 6);
    onChange(next);
    if (digit && i < 5) refs.current[i + 1]?.focus();
  };
  return (
    <div className="flex w-full gap-[9px]">
      {Array.from({ length: 6 }, (_, i) => (
        <input
          key={i}
          ref={el => (refs.current[i] = el)}
          value={value[i] ?? ''}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={t('common.digit', { n: i + 1 })}
          maxLength={1}
          onChange={e => set(i, e.target.value.replace(/\D/g, '').slice(-1))}
          onKeyDown={e => {
            if (e.key === 'Backspace' && !value[i] && i > 0) refs.current[i - 1]?.focus();
          }}
          onPaste={e => {
            const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
            if (!digits) return;
            e.preventDefault();
            onChange(digits);
            refs.current[Math.min(digits.length, 5)]?.focus();
          }}
          className={`w-0 min-w-0 flex-1 rounded-[10px] border border-[#C4CCD6] text-center font-bold text-[#1A2130] outline-none focus:border-[1.5px] focus:border-[#0C51E6] focus:shadow-[0_0_0_5px_rgba(12,81,230,.12)] ${
            large ? 'h-[60px] text-[24px]' : 'h-[52px] text-[20px]'
          }`}
        />
      ))}
    </div>
  );
};

export const Card = ({ width, children }: { width: number; children: React.ReactNode }) => (
  <div
    className="flex w-full flex-col gap-[18px] rounded-[16px] border border-[#EEF2F7] bg-white p-6"
    style={{ maxWidth: width }}
  >
    {children}
  </div>
);

export const Heading = ({ title, children }: { title: string; children?: React.ReactNode }) => (
  <div className="flex flex-col gap-[5px]">
    <h1 className="m-0 text-[20px] font-bold tracking-[-.01em] md:text-[24px]">{title}</h1>
    {children && <p className="m-0 text-[14px] leading-[1.5] text-[#5B6472]">{children}</p>}
  </div>
);
